'use client';

import { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { Icon } from '@iconify/react';
import { useRouter } from 'next/navigation';
import { ICONS, ROUTES } from '@/shared/constants';
import { BugReportDialog } from '@/features/bug-report/components/BugReportDialog';

interface HelpMenuItem {
  id: string;
  label: string;
  icon: string;
  disabled?: boolean;
}

const helpMenuItems: HelpMenuItem[] = [
  { id: 'get-started', label: 'Get Started', icon: ICONS.GET_STARTED, disabled: true },
  { id: 'faq', label: 'FAQ', icon: ICONS.FAQ, disabled: true },
  { id: 'tutorials', label: 'Tutorials', icon: ICONS.TUTORIALS, disabled: true },
];

export function HelpMenu() {
  const router = useRouter();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReportIssue = () => {
    handleClose();
    setDialogOpen(true);
  };

  return (
    <>
      <Tooltip title="Help">
        <IconButton
          color="inherit"
          aria-label="help menu"
          aria-controls={open ? 'help-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          <Icon icon={ICONS.HELP} width={24} height={24} />
        </IconButton>
      </Tooltip>
      <Menu
        id="help-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'help-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              mt: 1,
            },
          },
        }}
      >
        {helpMenuItems.map((item) => (
          <MenuItem key={item.id} disabled={item.disabled}>
            <ListItemIcon>
              <Icon icon={item.icon} width={20} height={20} />
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
            {item.disabled && (
              <Chip label="soon" size="small" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.675rem' }} />
            )}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleReportIssue}>
          <ListItemIcon>
            <Icon icon="material-symbols:feedback" width={20} height={20} />
          </ListItemIcon>
          <ListItemText>Report an Issue</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleClose(); router.push(ROUTES.DASHBOARD.MY_TICKETS.ROOT); }}>
          <ListItemIcon>
            <Icon icon={ICONS.MY_TICKETS} width={20} height={20} />
          </ListItemIcon>
          <ListItemText>My Tickets</ListItemText>
        </MenuItem>
      </Menu>

      {dialogOpen && (
        <BugReportDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
}
